export interface PaymentErrorMessage {
    title: string;
    description: string;
}

interface PaymentApiErrorResponse {
    error?: {
        outcome?: {
            message?: string;
            error?: string;
            gateway?: Record<string, string[]>;
        };
        debug?: { exception?: { response?: { outcome?: { message?: string } } } };
        message?: string;
    };
    message?: string;
}

const SEGMENT_DELIMITER = ' - ';
const SENTENCE_DELIMITER = '. ';
const HTTP_STATUS_TEXT_PATTERN = /^[A-Z][A-Z_]+$/;
const GATEWAY_TRANSLATION_PREFIX = 'APP_FORM.PAYMENT.ERRORS.GATEWAY.';
const VALIDATION_TRANSLATION_PREFIX = 'APP_FORM.PAYMENT.ERRORS.VALIDATION.';

export function parsePaymentErrorMessage(
    error: PaymentApiErrorResponse,
    defaultTitle: string,
    defaultDescription: string,
    translate?: (key: string) => string,
): PaymentErrorMessage {
    const defaults: PaymentErrorMessage = { title: defaultTitle, description: defaultDescription };

    // Shape A: Gateway field errors — outcome.gateway is an object of field → code arrays
    const gateway = error?.error?.outcome?.gateway;
    if (gateway && typeof gateway === 'object') {
        const firstFieldErrors = Object.values(gateway)[0];
        if (translate && Array.isArray(firstFieldErrors) && firstFieldErrors[0]) {
            const translationKey = GATEWAY_TRANSLATION_PREFIX + firstFieldErrors[0];
            const translated = translate(translationKey);
            if (translated !== translationKey) {
                return { title: defaultTitle, description: withInstructionalSuffix(translated, defaultDescription) };
            }
        }
        return defaults;
    }

    // Shape C: String message/error in outcome — segment-based parsing
    const outcomeMessage = error?.error?.outcome?.message ?? error?.error?.outcome?.error;
    if (outcomeMessage && typeof outcomeMessage === 'string' && !HTTP_STATUS_TEXT_PATTERN.test(outcomeMessage.trim())) {
        return parseSegments(outcomeMessage, defaultTitle, defaultDescription);
    }

    // Shape B / fallback: message field — check for validation keys first
    const message = error?.error?.debug?.exception?.response?.outcome?.message ?? error?.error?.message ?? error?.message ?? '';

    if (!message) {
        return defaults;
    }

    // Check for Laravel validation keys (e.g. "validation.credit_card.card_expiration_date_invalid (and 1 more error)")
    const firstToken = message.split(' ')[0];
    if (translate && firstToken.startsWith('validation.')) {
        const suffix = firstToken.replace('validation.', '').toUpperCase();
        const translationKey = VALIDATION_TRANSLATION_PREFIX + suffix;
        const translated = translate(translationKey);
        if (translated !== translationKey) {
            return { title: defaultTitle, description: withInstructionalSuffix(translated, defaultDescription) };
        }
        return defaults;
    }

    // Skip HTTP status texts like "UNPROCESSABLE_ENTITY"
    if (HTTP_STATUS_TEXT_PATTERN.test(message.trim())) {
        return defaults;
    }

    return parseSegments(message, defaultTitle, defaultDescription);
}

/**
 * Extracts the instructional suffix from defaultDescription (everything after the first sentence)
 * and appends it to the dynamic error.
 * e.g. "The credit card number is invalid." + "The X could not be verified. Please correct..."
 *    → "The credit card number is invalid. Please correct..."
 */
function withInstructionalSuffix(dynamicError: string, defaultDescription: string): string {
    const sentences = defaultDescription.split(SENTENCE_DELIMITER);
    const suffix = sentences.slice(1).join(SENTENCE_DELIMITER);
    return suffix ? `${dynamicError} ${suffix}` : dynamicError;
}

function parseSegments(rawMessage: string, defaultTitle: string, defaultDescription: string): PaymentErrorMessage {
    const rawSegments = rawMessage.split(SEGMENT_DELIMITER);
    const lastSegment = rawSegments.pop() ?? '';
    const [firstSentence, ...remainingSentences] = lastSegment.split(SENTENCE_DELIMITER);

    const titleSegments = [...rawSegments, firstSentence].filter(Boolean);
    const titleText = titleSegments.length ? titleSegments.join(SEGMENT_DELIMITER) : defaultTitle;
    const descriptionText = remainingSentences.join(SENTENCE_DELIMITER) || defaultDescription;

    return { title: titleText, description: descriptionText };
}
